import { useEffect, useRef, type ComponentType } from 'react';
import FeexPay from 'react-sdk-feexpay';

// Le SDK officiel FeexPay (react-sdk-feexpay) expose un bouton qui ouvre la
// modale de paiement hébergée : Mobile Money / Carte bancaire / Wallet, avec
// la sélection du pays et de l'opérateur. On garde une signature souple pour
// ne pas dépendre de la version exacte des types du paquet.
const Widget = FeexPay as unknown as ComponentType<Record<string, unknown>>;

export interface FeexPayWidgetState {
  /** Référence interne envoyée à FeexPay (idempotence + appariement webhook). */
  reference: string;
  /** Abonnement concerné (tunnel d'abonnement). */
  subscriptionId?: number;
  /** Commande / réservation concernée (tunnel boutique ou réservation). */
  entityId?: number;
  /** Origine de la transaction, transmise au webhook. */
  source?: string;
  /** Montant dans la devise réellement encaissée (XOF / XAF). */
  amount: number;
  currency: string;
  shopId: string;
  token: string;
  description: string;
  payCase: '' | 'MOBILE' | 'CARD' | 'WALLET';
  firstName: string;
  email: string;
  phone: string;
  country: string;
  network: string;
}

interface FeexPayModalProps {
  state: FeexPayWidgetState;
  onResult: (result: unknown) => void;
  onFallback: () => void;
}

/**
 * Ouvre la vraie modale de paiement FeexPay dans la page (aucune redirection).
 *
 * Le SDK ne fournit pas d'API programmatique : son composant est un bouton.
 * On rend donc ce bouton hors écran puis on le déclenche automatiquement,
 * ce qui ouvre la modale dès l'arrivée sur l'étape de paiement.
 */
export default function FeexPayModal({ state, onResult, onFallback }: FeexPayModalProps) {
  const holderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    let cancelled = false;
    let attempts = 0;

    const trigger = () => {
      if (cancelled) return;
      const button = holder.querySelector('button');
      if (button instanceof HTMLElement) {
        button.click();
        return;
      }
      attempts += 1;
      if (attempts < 12) window.setTimeout(trigger, 350);
    };

    const timer = window.setTimeout(trigger, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state.reference]);

  const countryField = state.country ? { country_iban: state.country } : {};
  const networkField = state.network ? { network: state.network.toUpperCase() } : {};
  const phoneField = state.phone ? { phone: state.phone } : {};

  return (
    <>
      <div ref={holderRef} aria-hidden="true" className="fixed left-[-9999px] top-0">
        <Widget
          id={state.shopId}
          token={state.token}
          amount={state.amount}
          currency={state.currency}
          description={state.description.slice(0, 40)}
          reference={state.reference}
          mode="LIVE"
          case={state.payCase}
          first_name={state.firstName}
          email={state.email}
          fieldsToHide={[]}
          defaultValueField={{ ...countryField, ...networkField, ...phoneField }}
          callback_info={{
            reference: state.reference,
            subscription_id: state.subscriptionId ?? null,
            entity_id: state.entityId ?? null,
            source: state.source || 'zifek_subscription',
          }}
          buttonText="Payer"
          callback={(result: unknown) => onResult(result)}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs">
        <span className="flex items-center gap-1.5 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-primary-500"></i>
          Ouverture de la fenêtre de paiement FeexPay…
        </span>
        <button
          type="button"
          onClick={onFallback}
          className="px-3 py-1.5 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 font-medium whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
        >
          <i className="ri-external-link-line mr-1.5"></i>
          La fenêtre ne s’ouvre pas ? Payer sur la page FeexPay
        </button>
      </div>
    </>
  );
}